<template>
  <transactions-table
    :is-transactions-loading="isTransactionsLoading"
    class="bills-table"
    grid-template-columns="80px 1fr 1.1fr 1fr 1fr 1.2fr 0.9fr minmax(120px, 1.15fr) 60px"
  >
    <template #tableHeader>
      <transactions-table-header
        class="transactions-table__header"
        :header-cols="$options.HEADER_COLS"
        @sortChanged="handleSortChanged"
      ></transactions-table-header>
    </template>
    <template #transactionItems>
      <bills-table-item
        :transaction="transaction"
        v-for="transaction in transactionsPaged"
        :key="transaction.operationId"
      ></bills-table-item>
    </template>
    <template #tablePagination>
      <transactions-table-pagination
        @changePage="handlePageChanged"
        :current-page="currentPage"
        :items-count="transactionsFiltered.length"
        :per-page="perPage"
      ></transactions-table-pagination>
    </template>
  </transactions-table>
</template>

<script>
import TransactionsTable from '@vue-app/components/company/transactions/table/TransactionsTable.vue'
import TransactionsTableHeader from '@vue-app/components/company/transactions/table/TransactionsTableHeader.vue'
import TransactionTableItem from '@vue-app/components/company/transactions/table/TransactionsTableItem.vue'
import { Transaction } from '@js-app/entities/transaction/Transaction'
import moment from 'moment'
import TransactionsTablePagination from '@vue-app/components/company/transactions/table/TransactionsTablePagination.vue'
import BillsTableItem from '@vue-app/components/company/bills/table/item/BillsTableItem.vue'

export default {
  name: 'CompanyBillsTableV2',
  components: {
    BillsTableItem,
    TransactionsTablePagination,
    TransactionTableItem,
    TransactionsTableHeader,
    TransactionsTable,
  },
  props: {
    transactionsList: {
      type: Array,
      default: () => [],
    },
    billType: {
      type: Object,
      default: null,
    },
    isTransactionsLoading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    currentPage: 1,
    perPage: 10,
    sort: {
      col: null,
      dir: null,
    },
  }),

  HEADER_COLS: [
    {
      id: 'date',
      hasSort: true,
      sortKey: 'date',
      title: 'bills.table.column.date',
      additionalClasses: ['transactions-table-header__col--date'],
    },
    {
      id: 'partnerTransactionId',
      hasSort: true,
      sortKey: 'partnerTransactionId',
      title: 'bills.table.column.partner-transaction-id',
      additionalClasses: ['transactions-table-header__col--pt-id'],
    },
    {
      id: 'operationId',
      hasSort: true,
      sortKey: 'operationId',
      title: 'bills.table.column.operation-id',
      additionalClasses: ['transactions-table-header__col--op-id'],
    },
    {
      id: 'order',
      hasSort: true,
      sortKey: 'order',
      title: 'bills.table.column.number',
      additionalClasses: ['transactions-table-header__col--number'],
    },
    {
      id: 'phoneEmail',
      hasSort: true,
      sortKey: 'phoneEmail',
      title: 'bills.table.column.phone_email',
      additionalClasses: ['transactions-table-header__col--phone-email'],
    },
    {
      id: 'recipient',
      hasSort: true,
      sortKey: 'recipient',
      title: 'bills.table.column.recipient',
      additionalClasses: ['transactions-table-header__col--recipient'],
    },
    {
      id: 'amount',
      hasSort: true,
      sortKey: 'amount',
      title: 'bills.table.column.amount',
      additionalClasses: ['transactions-table-header__col--amount'],
    },
    {
      id: 'status',
      hasSort: true,
      sortKey: 'status',
      title: 'bills.table.column.status',
      additionalClasses: ['transactions-table-header__col--status'],
    },
    {
      id: 'action',
      hasSort: false,
      title: 'bills.table.column.action',
      additionalClasses: ['transactions-table-header__col--action'],
    },
  ],

  computed: {
    transactionsPaged() {
      let _startFrom = (this.currentPage - 1) * this.perPage
      return this.transactionsFiltered.slice(
        _startFrom,
        _startFrom + this.perPage
      )
    },
    transactionsFiltered() {
      let _transactionToProcess = [...this.transactionsSorted]

      if (this.billType) {
        _transactionToProcess = _transactionToProcess.filter((t) =>
          this.billType.operationTypes.includes(t.operationTypeId)
        )
      }

      this.currentPage = 1

      return _transactionToProcess
    },
    transactionsSorted() {
      if (!this.sort || !this.sort.col) {
        return this.transactions
      }

      return [...this.transactions].sort((fTransaction, sTransaction) => {
        let _firstGrater = false

        switch (this.sort.col) {
          case 'date': {
            let _firstDate = moment(fTransaction.operationDate)
            let _secondDate = moment(sTransaction.operationDate)

            if (_firstDate > _secondDate) {
              _firstGrater = true
            }
            break
          }
          case 'section': {
            if (fTransaction.operationTypeId > sTransaction.operationTypeId) {
              _firstGrater = true
            }

            break
          }
          case 'operationID': {
            if (fTransaction.operationId > sTransaction.operationId) {
              _firstGrater = true
            }

            break
          }
          case 'client': {
            if (fTransaction.userFullName > sTransaction.userFullName) {
              _firstGrater = true
            }

            break
          }
          case 'transactionType': {
            if (fTransaction.transactionType > sTransaction.transactionType) {
              _firstGrater = true
            }

            break
          }
          case 'partnerTransactionId': {
            if (
              fTransaction.partnerTransactionId >
              sTransaction.partnerTransactionId
            ) {
              _firstGrater = true
            }

            break
          }
          case 'amount': {
            if (
              parseInt(fTransaction.transactionAmountPlain) >
              parseInt(sTransaction.transactionAmountPlain)
            ) {
              _firstGrater = true
            }

            break
          }
          case 'currency': {
            if (
              parseInt(fTransaction.transactionCurrencyPlain) >
              parseInt(sTransaction.transactionCurrencyPlain)
            ) {
              _firstGrater = true
            }

            break
          }
          case 'status': {
            if (
              parseInt(fTransaction.operationStatus) >
              parseInt(sTransaction.operationStatus)
            ) {
              _firstGrater = true
            }

            break
          }
        }

        switch (this.sort.dir) {
          case 'DESC': {
            if (_firstGrater) {
              return -1
            } else {
              return 1
            }
          }
          case 'ASC': {
            if (_firstGrater) {
              return 1
            } else {
              return -1
            }
          }
        }
      })
    },
    transactions() {
      return this.transactionsList.map(
        (_transObject) => new Transaction(_transObject)
      )
    },
  },
  methods: {
    handlePageChanged(page) {
      this.currentPage = page
    },
    handleSortChanged(newSort) {
      this.sort = { ...newSort }
    },
  },
}
</script>

<style lang="scss" scoped>
.bills-table {
  :deep(.transactions-table) {
    .transactions-table__header {
      padding: 30px 10px;
      background: #ffffff;
      border-radius: 20px 20px 0 0;
      border-bottom: 1px solid #eff2f8;
    }

    .transactions-table__body {
      background: #ffffff;
      border-radius: 0 0 20px 20px;
      padding-bottom: 30px;
    }
  }

  :deep(.transactions-table-header) {
    .transactions-table-header__col {
      .transactions-table-header__col--type {
        justify-content: center;
      }

      .transactions-table-header__col--amount {
        justify-content: flex-end;
      }

      .transactions-table-header__col--status {
        justify-content: center;
      }

      .transactions-table-header__col--action {
        justify-content: center;
      }
    }
  }
}
</style>
